import { useTranslation } from 'react-i18next';
import Button from '../Button';
import { PropsWithChildren, useMemo } from 'react';
import {
  differenceInCalendarMonths,
  differenceInCalendarYears,
  isSameMonth,
  isSameYear,
  startOfMonth,
  subYears,
} from 'date-fns';
import { useAppSelector, useTabs } from '../../hooks';
import './Datev.scss';
import { normalizeDateFE } from '@/core/normalization';
import { useLocation } from 'react-router';
import { DataUnit, DatevAggregatedItem, DatevFile } from '@/core/types';
import { activeCompanySelector } from '@/core/store/user/userSlice';
import { DatevTile } from './DatevTile';
import {
  AuthedRoutesEnum,
  DataCorrectionStatusEnum,
  DatevFileStandardEnum,
} from '@/core/enums';
import { DatevTileItem } from './NewDatevItem';

export interface Props {
  files: DatevFile[];
  titleClassName?: string;
  companyId: string;
  displaySinceISO?: string;
  aggregatedDatev?: DatevAggregatedItem[];
  className?: string;
  dataUnits: DataUnit[] | undefined;
}

export default function NewDatev({
  files,
  titleClassName = '',
  children,
  companyId,
  displaySinceISO,
  aggregatedDatev,
  className,
  dataUnits,
}: PropsWithChildren<Props>) {
  const { t } = useTranslation();
  const location = useLocation();
  const activeCompany = useAppSelector(activeCompanySelector);

  const years = useMemo(() => {
    if (!displaySinceISO) return [];

    const currDate = new Date();

    const diffCalendarYears = differenceInCalendarYears(
      currDate,
      normalizeDateFE(displaySinceISO)
    );

    const years = [
      ...(diffCalendarYears > 0
        ? Array(diffCalendarYears)
            .fill(1)
            .map((n, i) => subYears(currDate, i + n))
            .map((d) => d.getFullYear())
        : []),
    ];

    if (currDate.getMonth() > 0) {
      years.unshift(currDate.getFullYear());
    }

    return years;
  }, [displaySinceISO]);

  const dates = useMemo(() => {
    return years
      .map((v) =>
        Array(12)
          .fill(v)
          .map((v, i) => startOfMonth(new Date(v, i)))
      )
      .flat()
      .filter((d) => {
        const currentYear = new Date().getFullYear();
        const targetYear = +d.getFullYear();

        return (
          !!displaySinceISO &&
          differenceInCalendarMonths(d, normalizeDateFE(displaySinceISO)) >=
            0 &&
          isSameYear(targetYear, currentYear) &&
          differenceInCalendarMonths(new Date(), d) > 0
        );
      })
      .sort((a, b) => {
        return b.getTime() - a.getTime();
      });
  }, [years, displaySinceISO]);

  const tabs = ['All years', ...years];

  const { activeTab, onClickTab } = useTabs(tabs[0]);

  const data = useMemo(() => {
    return dates.map((d) => {
      const counts = (aggregatedDatev || [])
        .filter(
          (ad) => ad.month === d.getMonth() + 1 && ad.year === d.getFullYear()
        )
        .reduce(
          (
            acc: {
              [key: number]: number;
            },
            item
          ) => {
            acc[item.entry_type] = item.count;

            return acc;
          },
          {}
        );

      const filteredFiles = (files || []).filter((r) => {
        const date = normalizeDateFE(r.service_date);
        return isSameMonth(d, date);
      });
      const itemsMap = filteredFiles.reduce(
        (acc, f) => {
          acc[f.option] = {
            option: f.option,
            asciiFilename:
              acc[f.option]?.asciiFilename ||
              (f.standard === DatevFileStandardEnum.ascii ? f.file : ''),
            extfFilename:
              acc[f.option]?.extfFilename ||
              (f.standard === DatevFileStandardEnum.extf ? f.file : ''),
            asciiFileId:
              acc[f.option]?.asciiFileId ||
              (f.standard === DatevFileStandardEnum.ascii ? f.id : 0),
            extfFileId:
              acc[f.option]?.extfFileId ||
              (f.standard === DatevFileStandardEnum.extf ? f.id : 0),
          };
          return acc;
        },
        {} as Record<string, DatevTileItem>
      );

      return {
        month: d,
        items: Object.values(itemsMap),
        counts,
        files: filteredFiles,
      };
    });
  }, [dates, files, aggregatedDatev]);

  const params = new URLSearchParams(location.search);
  params.set('company_id', companyId || activeCompany!.company_id);

  return (
    <div
      className={`mx-auto flex w-full max-w-[874px] flex-grow flex-col rounded-lg bg-white px-6 py-4 ${className}`}
    >
      {children}
      <div className='flex items-start justify-between'>
        <div className={`mb-2 text-h3 font-black ${titleClassName}`}>
          {t('DATEV Export')}
        </div>

        {/* <div>Last updated: 09.12.2024 10:30 • Seller</div> */}
        {tabs.length > 2 && (
          <div className='flex items-center gap-2'>
            {tabs.map((v: string | number) => (
              <Button
                key={v}
                size='sm'
                styling={activeTab !== v ? 'outline' : undefined}
                onClick={onClickTab(v)}
              >
                {typeof v === 'string' ? t(v) : v}
              </Button>
            ))}
          </div>
        )}
      </div>
      <div className='mb-2'>
        {t(
          'Please note: Updates to the chart of accounts are effecting the next generation of exports.'
        )}
      </div>
      <div className='space-y-2'>
        {(data || [])
          .filter((d) => {
            if (activeTab === 'All years') return true;
            return d.month.getFullYear() === activeTab;
          })
          .map((d) => {
            const du = dataUnits?.find((du) =>
              isSameMonth(d.month, normalizeDateFE(du.date))
            );

            return (
              <DatevTile
                key={d.month.toISOString()}
                tile={d}
                companyId={activeCompany?.company_id || ''}
                to={{
                  pathname: AuthedRoutesEnum.DatevOverview.replace(
                    ':year',
                    String(d.month.getFullYear())
                  ).replace(':month', String(d.month.getMonth() + 1)),
                  search: params.toString(),
                }}
                isGeneratingAllowed={
                  du?.correction_status === DataCorrectionStatusEnum.Blocked
                }
              />
            );
          })}
      </div>
    </div>
  );
}
